"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Image from "next/image";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";

interface LazyNFTMediaProps {
	src: string;
	alt: string;
	isSelected: boolean;
	isCompressed?: boolean;
	width?: number;
	height?: number;
	quality?: number;
	loading?: "lazy" | "eager";
	className?: string;
	priority?: boolean;
}

type MediaType = "image" | "video";

function getMediaType(url: string): MediaType {
	if (!url) return "image";
	const lower = url.toLowerCase();
	if (lower.match(/\.(mp4|webm|ogg|avi|mov|wmv|flv|m4v)(\?|$)/)) return "video";
	return "image";
}

function resolveIpfs(url: string): string {
	return url;
}

function clampQuality(value: number): number {
	return Math.min(Math.max(Math.round(value), 10), 100);
}

function clampWidth(value: number): number {
	return Math.min(Math.max(Math.round(value), 1), 1024);
}

function buildProxyUrl(src: string, width: number, quality: number): string {
	const params = new URLSearchParams({
		url: src,
		w: String(clampWidth(width)),
		q: String(clampQuality(quality)),
	});
	return `/api/image-proxy?${params.toString()}`;
}

interface MediaSources {
	primary: string;
	fallback: string;
	placeholder: string;
	type: MediaType;
}

// Keep loaded media URLs across mounts to prevent flashing skeletons.
const loadedMediaCache = new Set<string>();

function buildMediaSources(
	src: string,
	displayWidth: number,
	displayQuality: number,
): MediaSources {
	if (!src) return { primary: "", fallback: "", placeholder: "", type: "image" };
	const type = getMediaType(src);
	const resolved = resolveIpfs(src);
	if (type === "video") {
		return { primary: resolved, fallback: resolved, placeholder: "", type };
	}
	const fullWidth = clampWidth(displayWidth);
	const fullQuality = clampQuality(displayQuality);
	const fallbackWidth = clampWidth(Math.max(64, Math.round(fullWidth * 0.6)));
	const fallbackQuality = clampQuality(Math.max(30, fullQuality - 20));

	return {
		primary: buildProxyUrl(resolved, fullWidth, fullQuality),
		fallback: buildProxyUrl(resolved, fallbackWidth, fallbackQuality),
		placeholder: buildProxyUrl(resolved, 20, 20),
		type,
	};
}

const LazyNFTMedia = ({
	src,
	alt,
	isSelected,
	isCompressed = false,
	width = 300,
	height = 300,
	quality = 75,
	loading = "lazy",
	className = "",
	priority = false,
}: LazyNFTMediaProps) => {
	const {
		primary: initialPrimary,
		placeholder: initialPlaceholder,
		type: initialType,
	} = buildMediaSources(src, width, quality);
	const isInitiallyLoaded = !!initialPrimary && loadedMediaCache.has(initialPrimary);

	const [mediaSrc, setMediaSrc] = useState<string>(initialPrimary);
	const [placeholderSrc, setPlaceholderSrc] = useState<string>(initialPlaceholder);
	const [mediaType, setMediaType] = useState<MediaType>(initialType);
	const [isLoading, setIsLoading] = useState(initialType === "video" && !isInitiallyLoaded);
	const [isFullLoaded, setIsFullLoaded] = useState(isInitiallyLoaded);
	const [showPlaceholder, setShowPlaceholder] = useState(
		initialType !== "video" && !isInitiallyLoaded,
	);
	const [hasError, setHasError] = useState(!src);
	const [isPlaying, setIsPlaying] = useState(false);
	const [isMuted, setIsMuted] = useState(true);
	const [showControls, setShowControls] = useState(false);
	const videoRef = useRef<HTMLVideoElement>(null);
	const triedFallbackRef = useRef(false);
	const responsiveSizes =
		width <= 64
			? "64px"
			: width <= 128
				? "(max-width: 768px) 30vw, 128px"
				: "(max-width: 768px) 100vw, 50vw";

	useEffect(() => {
		if (!src) {
			setHasError(true);
			setIsLoading(false);
			setIsFullLoaded(false);
			setShowPlaceholder(false);
			return;
		}
		const { primary, placeholder, type } = buildMediaSources(src, width, quality);
		const alreadyLoaded = loadedMediaCache.has(primary);
		triedFallbackRef.current = false;
		setMediaSrc(primary);
		setPlaceholderSrc(placeholder);
		setMediaType(type);
		setIsLoading(type === "video" && !alreadyLoaded);
		setIsFullLoaded(alreadyLoaded);
		setShowPlaceholder(type !== "video" && !alreadyLoaded);
		setHasError(false);
	}, [src, width, quality]);

	const handleError = useCallback(() => {
		if (mediaType === "video") {
			setHasError(true);
			setIsLoading(false);
			return;
		}

		const { fallback } = buildMediaSources(src, width, quality);
		if (!triedFallbackRef.current && fallback && fallback !== mediaSrc && !hasError) {
			triedFallbackRef.current = true;
			setMediaSrc(fallback);
			setIsFullLoaded(false);
			setShowPlaceholder(true);
		} else {
			setHasError(true);
			setIsLoading(false);
		}
	}, [src, width, quality, mediaSrc, mediaType, hasError]);

	const togglePlay = useCallback(() => {
		if (videoRef.current) {
			if (isPlaying) {
				videoRef.current.pause();
			} else {
				videoRef.current.play();
			}
			setIsPlaying(!isPlaying);
		}
	}, [isPlaying]);

	const toggleMute = useCallback(() => {
		if (videoRef.current) {
			videoRef.current.muted = !isMuted;
			setIsMuted(!isMuted);
		}
	}, [isMuted]);

	const renderMedia = () => {
		if (hasError && !placeholderSrc) {
			return (
				<div className="absolute inset-0 bg-[#2A2A2A] flex flex-col items-center justify-center">
					<svg
						className="w-12 h-12 text-gray-600 mb-2"
						fill="none"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
					</svg>
					<span className="text-xs text-gray-500">Failed to load media</span>
				</div>
			);
		}

		if (mediaType === "video") {
			return (
				<div className="relative w-full h-full">
					<video
						ref={videoRef}
						src={mediaSrc}
						title={alt}
						aria-label={alt}
						className={`w-full h-full object-contain transition-opacity duration-300 ${
							isLoading ? "opacity-0" : "opacity-100"
						}`}
						muted={isMuted}
						loop
						playsInline
						preload="metadata"
						onLoadedData={() => {
							loadedMediaCache.add(mediaSrc);
							setIsLoading(false);
						}}
						onError={() => {
							setHasError(true);
							setIsLoading(false);
						}}
						onPlay={() => setIsPlaying(true)}
						onPause={() => setIsPlaying(false)}
						onMouseEnter={() => setShowControls(true)}
						onMouseLeave={() => setShowControls(false)}
					/>

					{showControls && (
						<div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-200">
							<div className="absolute bottom-2 left-2 right-2 flex justify-between items-center">
								<button
									onClick={togglePlay}
									aria-label={isPlaying ? "Pause video" : "Play video"}
									className="bg-black bg-opacity-60 text-white p-2 rounded-full hover:bg-opacity-80 transition-all"
								>
									{isPlaying ? <Pause size={16} /> : <Play size={16} />}
								</button>
								<button
									onClick={toggleMute}
									aria-label={isMuted ? "Unmute video" : "Mute video"}
									className="bg-black bg-opacity-60 text-white p-2 rounded-full hover:bg-opacity-80 transition-all"
								>
									{isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
								</button>
							</div>
						</div>
					)}

					<div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
						VIDEO
					</div>
				</div>
			);
		}

		// Images are loaded through the proxy with a tiny blurred placeholder first.
		return (
			<div className="absolute inset-0">
				{placeholderSrc && (
					<Image
						src={placeholderSrc}
						alt=""
						fill
						sizes={responsiveSizes}
						quality={20}
						loading={priority ? undefined : "eager"}
						priority={priority}
						aria-hidden
						className={`object-contain transition-all duration-300 ${
							isFullLoaded && !hasError && !showPlaceholder
								? "opacity-0 blur-0 scale-100"
								: "opacity-100 blur-[8px] scale-110"
						}`}
					/>
				)}

				{mediaSrc && (
					<Image
						src={mediaSrc}
						alt={alt}
						fill
						sizes={responsiveSizes}
						quality={quality}
						loading={priority ? undefined : loading}
						priority={priority}
						className={`object-contain transition-opacity duration-300 ${
							isFullLoaded && !hasError ? "opacity-100" : "opacity-0"
						}`}
						onLoad={() => {
							loadedMediaCache.add(mediaSrc);
							setIsLoading(false);
							setIsFullLoaded(true);
							setTimeout(() => setShowPlaceholder(false), 180);
						}}
						onError={handleError}
					/>
				)}

				{hasError && (
					<div className="absolute inset-0 bg-black/35 flex flex-col items-center justify-center">
						<svg
							className="w-12 h-12 text-gray-200 mb-2"
							fill="none"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="2"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
						</svg>
						<span className="text-xs text-gray-100">Failed to load media</span>
					</div>
				)}
			</div>
		);
	};

	return (
		<div className={`relative w-full h-full ${className}`}>
			{/* Skeleton visible while image is loading; disappears on onLoad */}
			{isLoading && mediaType === "video" && !hasError && (
				<div className="absolute inset-0 bg-[#2A2A2A] animate-pulse rounded-lg" />
			)}

			{renderMedia()}
		</div>
	);
};

export default LazyNFTMedia;
